import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Mic,
  Phone,
  Calendar,
  ChevronRight,
  Trash2,
  Loader2
} from "lucide-react";

export default function History() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: null });
  const [deleting, setDeleting] = useState(false);

  const canDelete = user?.is_manager || user?.is_admin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, analysesRes] = await Promise.all([
        fetch(`${API}/sessions`, { credentials: "include" }),
        fetch(`${API}/analyze/history`, { credentials: "include" })
      ]);

      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setSessions(sessionsData);
      }

      if (analysesRes.ok) {
        const analysesData = await analysesRes.json();
        setAnalyses(analysesData);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteDialog.id) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/sessions/${deleteDialog.id}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        setSessions(prev => prev.filter(s => s.session_id !== deleteDialog.id));
        toast.success("Session deleted", { description: "The roleplay session has been permanently removed" });
      } else {
        const error = await response.json();
        toast.error("Failed to delete", { description: error.detail });
      }
    } catch (error) {
      toast.error("Failed to delete session");
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, type: null, id: null, name: null });
    }
  };

  const handleDeleteAnalysis = async () => {
    if (!deleteDialog.id) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/analyze/${deleteDialog.id}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        setAnalyses(prev => prev.filter(a => a.analysis_id !== deleteDialog.id));
        toast.success("Analysis deleted", { description: "The call analysis has been permanently removed" });
      } else {
        const error = await response.json();
        toast.error("Failed to delete", { description: error.detail });
      }
    } catch (error) {
      toast.error("Failed to delete analysis");
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, type: null, id: null, name: null });
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">History</h1>
          <p className="text-muted-foreground mt-1">Review your past sessions and call analyses</p>
        </div>
        <Link to="/roleplay/new">
          <Button className="bg-primary hover:bg-primary/90">
            <Mic className="w-4 h-4 mr-2" /> New Session
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="roleplays" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="roleplays" data-testid="roleplays-tab">
            <Mic className="w-4 h-4 mr-2" />
            Roleplay Sessions ({sessions.length})
          </TabsTrigger>
          <TabsTrigger value="analyses" data-testid="analyses-tab">
            <Phone className="w-4 h-4 mr-2" />
            Call Analyses ({analyses.length})
          </TabsTrigger>
        </TabsList>

        {/* Roleplay Sessions */}
        <TabsContent value="roleplays" className="mt-6">
          {sessions.length > 0 ? (
            <div className="space-y-4">
              {sessions.map((session) => (
                <Card 
                  key={session.session_id} 
                  className="hover:border-primary transition-colors"
                  data-testid={`session-${session.session_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Link 
                        to={`/roleplay/results/${session.session_id}`}
                        className="flex items-center gap-4 flex-1"
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          session.status === "completed" 
                            ? "bg-green-500/10" 
                            : session.status === "active"
                            ? "bg-yellow-500/10"
                            : "bg-gray-500/10"
                        }`}>
                          <Mic className={`w-6 h-6 ${
                            session.status === "completed" 
                              ? "text-green-500" 
                              : session.status === "active"
                              ? "text-yellow-500"
                              : "text-gray-500"
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-medium">{session.scenario_name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {session.status}
                            </Badge>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(session.started_at)}
                            </span>
                          </div>
                        </div>
                      </Link>
                      <div className="flex items-center gap-4">
                        {session.score && (
                          <div className="text-right">
                            <p className={`font-mono text-2xl font-bold ${getScoreColor(session.score)}`}>
                              {session.score}
                            </p>
                            <p className="text-xs text-muted-foreground">score</p>
                          </div>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeleteDialog({
                                open: true,
                                type: "session",
                                id: session.session_id,
                                name: session.scenario_name
                              });
                            }}
                            data-testid={`delete-session-${session.session_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Link to={`/roleplay/results/${session.session_id}`}>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Mic className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No sessions yet</h3>
              <p className="text-muted-foreground mb-4">Start your first roleplay to see it here</p>
              <Link to="/roleplay/new">
                <Button>Start Roleplay</Button>
              </Link>
            </Card>
          )}
        </TabsContent>

        {/* Call Analyses */}
        <TabsContent value="analyses" className="mt-6">
          {analyses.length > 0 ? (
            <div className="space-y-4">
              {analyses.map((analysis) => (
                <Card 
                  key={analysis.analysis_id} 
                  className="hover:border-primary transition-colors"
                  data-testid={`analysis-${analysis.analysis_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Link 
                        to={`/analyze/results/${analysis.analysis_id}`}
                        className="flex items-center gap-4 flex-1"
                      >
                        <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                          <Phone className="w-6 h-6 text-orange-500" />
                        </div>
                        <div>
                          <h3 className="font-medium">{analysis.filename}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {analysis.analysis?.call_type && (
                              <Badge variant="secondary" className="text-xs">
                                {analysis.analysis.call_type.replace(/_/g, " ")}
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(analysis.created_at)}
                            </span>
                          </div>
                        </div>
                      </Link>
                      <div className="flex items-center gap-4">
                        {analysis.score && (
                          <div className="text-right">
                            <p className={`font-mono text-2xl font-bold ${getScoreColor(analysis.score)}`}>
                              {analysis.score}
                            </p>
                            <p className="text-xs text-muted-foreground">score</p>
                          </div>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeleteDialog({
                                open: true,
                                type: "analysis",
                                id: analysis.analysis_id,
                                name: analysis.filename
                              });
                            }}
                            data-testid={`delete-analysis-${analysis.analysis_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Link to={`/analyze/results/${analysis.analysis_id}`}>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Phone className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No analyses yet</h3>
              <p className="text-muted-foreground mb-4">Upload a call recording to get AI feedback</p>
              <Link to="/analyze">
                <Button>Analyze Call</Button>
              </Link>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteDialog.type === "session" ? "Session" : "Analysis"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete "{deleteDialog.name}"? This action cannot be undone and will remove all associated data including transcripts, scores, and feedback.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteDialog.type === "session" ? handleDeleteSession : handleDeleteAnalysis}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
